import * as CryptoJS from 'crypto-js';
import { environment } from '@env';

const SECRET = environment.routeParamSecret;

/**
 * Encode a route parameter (ID or any string) into a URL-safe
 * encrypted string using AES-256 + base64url encoding.
 *
 * Usage in component:
 *   router.navigate(['/builder/projects', encodeParam(project.id)]);
 *
 * Usage in template:
 *   [routerLink]="['/builder/projects', encodeParam(project.id)]"
 */
export function encodeParam(value: string | number): string {
  const encrypted = CryptoJS.AES.encrypt(String(value), SECRET).toString();
  // base64url: replace chars that are invalid in URLs
  return encrypted
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Decode a URL-safe encrypted route parameter back to its original value.
 *
 * Usage in component (after reading from ActivatedRoute):
 *   const id = decodeParam(this.route.snapshot.paramMap.get('id') ?? '');
 */
export function decodeParam(encoded: string): string {
  // Restore base64 padding and reserved chars
  const base64 = encoded
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    + '=='.slice(0, (4 - (encoded.length % 4)) % 4);

  try {
    const bytes = CryptoJS.AES.decrypt(base64, SECRET);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return '';
  }
}

/**
 * Convenience: decode and parse as a number.
 * Returns NaN if decoding fails or the result is not numeric.
 */
export function decodeParamAsNumber(encoded: string): number {
  return Number(decodeParam(encoded));
}
